import { useContext, useEffect, useState, useMemo } from "react";
import Select from "react-select";
import { useIncidents } from "../../../hooks/useIncidents";
import { getNameIncidentsOrigins, translate } from "../../../utils/Common";
import ThemeContext from "../../../contexts/theme";
import { getReactSelectStyles } from "../../../utils/reactSelectThemeStyles";
import { TbWorld } from "react-icons/tb";
import ignoreAbort from '../../../utils/ignoreAbort';
import { SelectShell } from "@/components/ui/select-shell";

const OriginIncidentList = ({
  register,
  errors,
  setValue,
  nameShow,
  idShow,
  readOnly,
  sizeInput,
  isRequired = true,
}) => {
  const themeContext = useContext(ThemeContext);
  const theme = themeContext?.theme || "light";
  const selectStyles = useMemo(() => getReactSelectStyles(theme, { hasIcon: true }), [theme]);
  const { originsIncidents, getOrigins } = useIncidents();
  const [originListIncidents, setOriginListIncidents] = useState(null);
  const [selectedOption, setSelectedOption] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const abortController = new AbortController();

    const handleOrigins = async () => {
      const response = await getOrigins(abortController.signal);
      setOriginListIncidents(response);
      setLoading(false);
    };

    if (!originsIncidents) {
      handleOrigins().catch(ignoreAbort);
    } else {
      setOriginListIncidents(originsIncidents);
      setLoading(false);
    }

    return () => abortController.abort();
  }, [getOrigins, originsIncidents]);

  const selectOptions = useMemo(() => {
    if (!originListIncidents || !Array.isArray(originListIncidents)) return [];
    return originListIncidents.map((item) => ({
      value: item.id,
      label: getNameIncidentsOrigins(item.id) || String(item.id),
      ...item,
    }));
  }, [originListIncidents]);

  const fieldName = "originIncident";
  const hiddenValue = selectedOption?.value ?? (idShow ?? "");

  return (
    <SelectShell icon={<TbWorld size={16} />} error={errors?.originIncident}>
        <Select
          value={selectedOption}
          options={selectOptions}
          isLoading={loading}
          classNamePrefix="react-select"
          placeholder={nameShow || translate("global.select")}
          name={fieldName}
          onChange={(e) => {
            setSelectedOption(e);
            if (setValue) {
              setValue(fieldName, e ? e.value : "", { shouldValidate: true });
            }
          }}
          isClearable
          isSearchable
          filterOption={(option, search) =>
            (option.label || "")
              .toLowerCase()
              .includes((search || "").toLowerCase())
          }
          noOptionsMessage={() => translate("global.noResults")}
          styles={selectStyles}
          isDisabled={readOnly}
        />
      <input
        type="hidden"
        name={fieldName}
        value={hiddenValue}
        ref={
          register &&
          register({
            validate: (value) =>
              !isRequired ||
              (value !== "" &&
                value !== translate("global.select") &&
                value !== translate("global.noResults")) ||
              translate("global.chooseValidOrigin"),
          })
        }
      />
    </SelectShell>
  );
};

export default OriginIncidentList;
